import { useEffect, useState } from "react";
import { STYX_THEME } from "../styles/theme";
import type { BootItem } from "../app/types";
import type { PwaBootUpdateUi } from "../lib/pwaBootUpdateStatus";
import { setPwaBootUpdate } from "../lib/pwaBootUpdateStatus";
import type { BootOverlayMode } from "../lib/pwaBootOverlayMode";
import { PWA_BOOT_LABELS, pwaBootRowState } from "../lib/pwaBootRegistration";

type BootScreenProps = {
  items: BootItem[];
  ready: boolean;
  error: string | null;
  /** Первая строка: проверка обновлений PWA; если не передать — строка скрыта. */
  pwaUpdate?: PwaBootUpdateUi | null;
  /** full — весь чеклист; sw-update-only — только блок обновления SW; post-sw-restart — один короткий статус после reload. */
  overlayMode?: BootOverlayMode;
};

const SLASH_TOTAL = 20;
const SLASH_TICK_MS = 55;

function markerFor(state: BootItem["state"]) {
  switch (state) {
    case "done":
      return "[ok]";
    case "active":
      return "[..]";
    case "error":
      return "[!!]";
    default:
      return "[  ]";
  }
}

function pwaLabel(phase: PwaBootUpdateUi["phase"]): string {
  switch (phase) {
    case "checking":
      return PWA_BOOT_LABELS.checking;
    case "current":
      return PWA_BOOT_LABELS.current;
    case "deploying":
    case "completed":
      return PWA_BOOT_LABELS.deploying;
    case "skipped":
      return PWA_BOOT_LABELS.skipped;
    default:
      return PWA_BOOT_LABELS.checking;
  }
}

export function BootScreen({
  items,
  ready,
  error,
  pwaUpdate,
  overlayMode = "full",
}: BootScreenProps) {
  const [deployFill, setDeployFill] = useState(0);

  useEffect(() => {
    if (!pwaUpdate || pwaUpdate.phase !== "deploying") {
      setDeployFill(0);
      return;
    }
    setDeployFill(0);
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      setDeployFill(n);
      if (n >= SLASH_TOTAL) {
        window.clearInterval(id);
        setPwaBootUpdate({ phase: "completed", detail: "COMPLETED" });
      }
    }, SLASH_TICK_MS);
    return () => window.clearInterval(id);
  }, [pwaUpdate?.phase]);

  const swUpdateOnly = overlayMode === "sw-update-only";
  const postSwRestart = overlayMode === "post-sw-restart";
  const showFullChecklist = overlayMode === "full";
  const showPwaRow = pwaUpdate != null && !postSwRestart;

  const headerSubtitle = swUpdateOnly
    ? "обновление клиента"
    : postSwRestart
      ? "запуск после обновления"
      : "инициализация узла";

  return (
    <div className={`boot-screen ${swUpdateOnly ? "boot-screen--sw-update-only" : ""} ${postSwRestart ? "boot-screen--post-sw-restart" : ""}`}>
      <div className="boot-screen__panel">
        <div className="boot-screen__scanlines" aria-hidden />
        <span className="boot-screen__corner boot-screen__corner--tl" aria-hidden>
          ┌
        </span>
        <span className="boot-screen__corner boot-screen__corner--tr" aria-hidden>
          ┐
        </span>
        <span className="boot-screen__corner boot-screen__corner--bl" aria-hidden>
          └
        </span>
        <span className="boot-screen__corner boot-screen__corner--br" aria-hidden>
          ┘
        </span>
        <div className="boot-screen__inner">
          <div className="boot-screen__header">
            <span className="boot-screen__brand">
              {STYX_THEME.appName}
              <span className="boot-screen__brand-sep"> // </span>
              {headerSubtitle}
            </span>
            <span className={`boot-screen__state ${ready ? "is-ready" : ""}`}>
              {ready ? "готово" : "инициализация"}
            </span>
          </div>
          <div className="boot-screen__body">
            {showPwaRow && pwaUpdate ? (
              <div
                className={`boot-line boot-line--pwa-update boot-line--${pwaBootRowState(
                  pwaUpdate.phase,
                )}`}
              >
                <span className="boot-line__marker">{markerFor(pwaBootRowState(pwaUpdate.phase))}</span>
                <span className="boot-line__label">{pwaLabel(pwaUpdate.phase)}</span>
                <span className="boot-line__detail boot-line__detail--pwa">
                  {pwaUpdate.phase === "deploying" ? (
                    <span className="boot-line__slash-meter" aria-hidden>
                      <span className="boot-line__slash-meter-fill">
                        {"/".repeat(deployFill)}
                      </span>
                      <span className="boot-line__slash-meter-rest">
                        {"/".repeat(Math.max(0, SLASH_TOTAL - deployFill))}
                      </span>
                    </span>
                  ) : (
                    pwaUpdate.detail
                  )}
                </span>
              </div>
            ) : null}
            {postSwRestart ? (
              <div className="boot-line boot-line--active">
                <span className="boot-line__marker">{markerFor(ready ? "done" : "active")}</span>
                <span className="boot-line__label">сессия и интерфейс</span>
                <span className="boot-line__detail">
                  {ready ? "готово" : "крипто, IndexedDB, сокет…"}
                </span>
              </div>
            ) : null}
            {showFullChecklist
              ? items.map((item) => (
                  <div key={item.label} className={`boot-line boot-line--${item.state}`}>
                    <span className="boot-line__marker">{markerFor(item.state)}</span>
                    <span className="boot-line__label">{item.label}</span>
                    <span className="boot-line__detail">{item.detail ?? ""}</span>
                  </div>
                ))
              : null}
            <div className="boot-line boot-line--cursor">
              <span className="boot-line__marker boot-line__marker--prompt">&gt;</span>
              <span className="boot-line__label-row">
                <span className="boot-line__label">
                  {error ?? (ready ? "переход в интерфейс" : "ожидание сигнала")}
                </span>
                <span className="boot-line__cursor-block" aria-hidden />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
